import { computed, ref } from "vue"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import type { LocationController } from "@/controllers/model-controllers/location-controller"
import { Location } from "@/models/location.model"
import { stripCountryFromLocationText } from "@/helper/location/location-helpers"

export const LOCATION_AUTOCOMPLETE_MIN_CHARS = 3

/** Prefer Dutch results without restricting autocomplete to the Netherlands only. */
export const LOCATION_AUTOCOMPLETE_REGION_BIAS = "nl"

/** Randstad viewport — same bounds as PNTS-2870 `location-maps.ts` / instruction feedback. */
export const LOCATION_AUTOCOMPLETE_RANDSTAD_BOUNDS = {
  south: 51.8,
  west: 4.1,
  north: 52.7,
  east: 5.4
} as const

export const LOCATION_AUTOCOMPLETE_LOCATION_BIAS = {
  rectangle: {
    low: {
      latitude: LOCATION_AUTOCOMPLETE_RANDSTAD_BOUNDS.south,
      longitude: LOCATION_AUTOCOMPLETE_RANDSTAD_BOUNDS.west
    },
    high: {
      latitude: LOCATION_AUTOCOMPLETE_RANDSTAD_BOUNDS.north,
      longitude: LOCATION_AUTOCOMPLETE_RANDSTAD_BOUNDS.east
    }
  }
}

export function buildGooglePlacesAutocompleteBody(input: string): Record<string, unknown> {
  return {
    input,
    regionCode: LOCATION_AUTOCOMPLETE_REGION_BIAS,
    languageCode: "nl",
    locationBias: LOCATION_AUTOCOMPLETE_LOCATION_BIAS
  }
}

const HOUSE_NUMBER_SUFFIX_REGEX = /^(.*?[^\d\s])\s+(\d+(?:[A-Za-z0-9/-]+)?)(?:\s+(.+))?$/i

export type LocationSuggestion = {
  key: string
  source: "saved" | "google"
  label: string
  name: string
  location?: Location
}

export function splitHouseNumberSuffix(
  query: string
): { streetQuery: string; houseNumber: string } | null {
  const match = query.trim().match(HOUSE_NUMBER_SUFFIX_REGEX)
  if (!match) return null
  const street = match[1]?.trim()
  const houseNumber = match[2]?.trim()
  const trailing = match[3]?.trim()
  if (!street || !houseNumber) return null
  const streetQuery = trailing ? `${street} ${trailing}` : street
  return { streetQuery, houseNumber }
}

export function getLocationSuggestionStreetName(suggestion: LocationSuggestion): string {
  return suggestion.label.split(",")[0]?.trim() || suggestion.label
}

export function parseHouseNumberEntry(
  query: string
): { streetPart: string; houseNumber: string; trailing?: string } | null {
  const trimmed = query.trim()
  const withSpace = trimmed.match(HOUSE_NUMBER_SUFFIX_REGEX)
  if (withSpace) {
    const streetPart = withSpace[1]?.trim()
    const houseNumber = withSpace[2]?.trim()
    const trailing = withSpace[3]?.trim()
    if (!streetPart || !houseNumber) return null
    return { streetPart, houseNumber, trailing: trailing || undefined }
  }

  const direct = trimmed.match(/^(.*[^\d\s])(\d+(?:[A-Za-z0-9/-]*)?)$/i)
  if (!direct) return null
  const streetPart = direct[1]?.trim()
  const houseNumber = direct[2]?.trim()
  if (!streetPart || !houseNumber) return null
  return { streetPart, houseNumber }
}

/** True when the user just started typing a house number after a street-only query. */
export function shouldApplyFirstSuggestionStreet(
  query: string,
  queryBeforeLastKey: string
): boolean {
  if (!parseHouseNumberEntry(query.trim())) return false
  return !parseHouseNumberEntry(queryBeforeLastKey.trim())
}

export function decorateSuggestionWithHouseNumber(
  suggestion: LocationSuggestion,
  houseNumber: string
): string {
  const street = suggestion.label.split(",")[0]?.trim() || suggestion.label
  const commaIndex = suggestion.label.indexOf(",")
  const withNumber = `${street} ${houseNumber}`
  return commaIndex === -1 ? withNumber : `${withNumber}${suggestion.label.slice(commaIndex)}`
}

export function mergeFirstSuggestionStreetWithHouseNumber(
  query: string,
  suggestion: LocationSuggestion
): string | null {
  const parsed = parseHouseNumberEntry(query.trim())
  if (!parsed) return null
  return `${getLocationSuggestionStreetName(suggestion)} ${parsed.houseNumber}`
}

function normalizeLocationSearchText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
}

/**
 * The saved-location suggestions whose name or address contain every word of `query`, so known
 * saved locations can be shown without waiting for a search.
 */
export function filterSavedLocationSuggestions(
  suggestions: Iterable<LocationSuggestion>,
  query: string,
  limit = 5
): LocationSuggestion[] {
  const words = normalizeLocationSearchText(query).split(/\s+/).filter(Boolean)
  return [...suggestions]
    .filter((suggestion) => {
      if (suggestion.source !== "saved") return false
      const text = normalizeLocationSearchText(
        `${suggestion.label} ${suggestion.location?.address ?? ""}`
      )
      return words.every((word) => text.includes(word))
    })
    .slice(0, limit)
}

/** Saved (named) locations matching `q`, or any five of them for an empty `q`. */
export async function fetchSavedLocations(
  locationController: LocationController | undefined,
  q: string,
  excludeId?: string
): Promise<LocationSuggestion[]> {
  if (!locationController) return []
  try {
    const locations = await locationController.getList({
      query: new ODataQueryBuilder().ne("name", null).search(q).top(5)
    })
    return locations
      .filter((l) => l.name && l.id !== excludeId)
      .map((l) => ({
        key: `saved-${l.id}`,
        source: "saved" as const,
        label: l.name as string,
        name: l.name as string,
        location: l
      }))
  } catch {
    return []
  }
}

async function fetchGooglePlaces(term: string, apiKey: string): Promise<LocationSuggestion[]> {
  if (term.length < LOCATION_AUTOCOMPLETE_MIN_CHARS || !apiKey) return []
  try {
    const res = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Goog-Api-Key": apiKey },
      body: JSON.stringify(buildGooglePlacesAutocompleteBody(term))
    })
    if (!res.ok) return []
    const data = await res.json()
    return (data.suggestions ?? [])
      .map((s: any, i: number): LocationSuggestion | null => {
        const prediction = s.placePrediction
        if (!prediction) return null
        const label = stripCountryFromLocationText(prediction.text?.text ?? "")
        return {
          key: `google-${prediction.placeId ?? i}`,
          source: "google" as const,
          label,
          name: label
        }
      })
      .filter((s: LocationSuggestion | null): s is LocationSuggestion => s !== null)
  } catch {
    return []
  }
}

export async function searchLocationSuggestions(
  query: string,
  options: {
    locationController?: LocationController
    apiKey: string
    excludeId?: string
    /**
     * Called with the suggestions found so far each time the saved-location or Maps search
     * answers, saved locations first; `savedPending` while the saved locations are not in yet.
     */
    onPartialResults?: (suggestions: LocationSuggestion[], savedPending: boolean) => void
  }
): Promise<LocationSuggestion[]> {
  const q = query.trim()
  const houseSuffix = splitHouseNumberSuffix(q)
  const googleTerm = houseSuffix ? houseSuffix.streetQuery : q
  if (q.length < LOCATION_AUTOCOMPLETE_MIN_CHARS) return []

  let saved: LocationSuggestion[] | null = null
  let google: LocationSuggestion[] = []
  const combined = () => [...(saved ?? []), ...google]
  await Promise.all([
    fetchSavedLocations(options.locationController, q, options.excludeId).then((result) => {
      saved = result
      options.onPartialResults?.(combined(), false)
    }),
    fetchGooglePlaces(googleTerm, options.apiKey).then((result) => {
      google = result.map((g) => decorateSuggestionWithHouseNumberItem(g, houseSuffix))
      options.onPartialResults?.(combined(), saved === null)
    })
  ])
  return combined()
}

function decorateSuggestionWithHouseNumberItem(
  suggestion: LocationSuggestion,
  houseSuffix: { streetQuery: string; houseNumber: string } | null
): LocationSuggestion {
  if (!houseSuffix) return suggestion
  const label = decorateSuggestionWithHouseNumber(suggestion, houseSuffix.houseNumber)
  return { ...suggestion, label, name: label }
}

/** Shared dropdown state + keyboard highlight for location-suggestion lists. */
export function useSuggestionList() {
  const open = ref(false)
  const loading = ref(false)
  const items = ref<LocationSuggestion[]>([])
  const highlightedIndex = ref(0)
  const query = ref("")
  const trimmedQuery = computed(() => query.value.trim())

  return { open, loading, items, highlightedIndex, query, trimmedQuery }
}
